import { ApplicationElement, html, nothing } from '../application_element/element.js';
import { UNAVAILABLE, formatValue } from '../../lib/chart_format.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * A single headline figure. Consumes the `scalar` data shape.
 *
 * A `null` value means the figure is unavailable rather than zero — a ratio
 * whose denominator was missing, for instance — and renders as a dash.
 *
 * @prop value - The figure ( number | null, default: null )
 * @prop label - What the figure counts ( string )
 * @prop caption - Optional supporting line under the value ( string )
 * @prop format - 'number' | 'percent' ( string, default: 'number' )
 * @prop precision - Fraction digits; defaults to 0 for numbers, 1 for percent ( number | null )
 *
 * @cssprop [--value-color: var(--gray-900, #212529)] - Colour of the figure.
 * @cssprop [--label-color: var(--gray-600, #868e96)] - Colour of the label.
 */
export default class StatPanel extends ApplicationElement {
  static properties = {
    value: { type: Number },
    label: { type: String },
    caption: { type: String },
    format: { type: String },
    precision: { type: Number },
  };

  static translations = translations;

  static styles = [ApplicationElement.styles, style];

  constructor() {
    super();
    this.value = null;
    this.format = 'number';
    this.precision = null;
  }

  get unavailable() {
    return this.value === null || this.value === undefined || Number.isNaN(this.value);
  }

  get formattedValue() {
    if (this.unavailable) return UNAVAILABLE;

    return formatValue(this.value, {
      locale: this.i18n.locale,
      format: this.format,
      precision: this.precision,
    });
  }

  render() {
    return html`
      <p class="label">${this.label}</p>
      <p
        class="value ${this.unavailable ? 'unavailable' : ''}"
        aria-label=${this.unavailable ? this.t('unavailable') : nothing}>
        ${this.formattedValue}
      </p>
      ${this.caption ? html`<p class="caption">${this.caption}</p>` : nothing}
    `;
  }
}

StatPanel.extendTranslationsAs('xs-stat-panel');

customElements.get('xs-stat-panel') ||
  customElements.define('xs-stat-panel', StatPanel);
